import { useState } from "react";
import { Divider, Dropdown, DropdownOption, TextField, Tooltip, Typography } from "@volvo/vcdk-react";
import { UITEXT, USER_FLEET_COUNTRIES } from "../../../../constants/const";
import NavigationButtons from "../NavigationButtons/NavigationButtons";
import type { UserInformationTabProps } from "../types";
import styles from "./UserInformationTab.module.css";

const UserInformationTab: React.FC<UserInformationTabProps> = ({
    formData,
    onNext,
    onBack,
    handleTextChange,
    handleFleetCountryChange,
    errors,
}) => {
    const [fleetNameToolTip, setFleetNameToolTip] =
        useState<HTMLElement | null>(null);
    const [fleetCountryTooltip, setFleetCountryTooltip] =
        useState<HTMLElement | null>(null);
    const [fleetAdminEmailToolTip, setFleetAdminEmailToolTip] =
        useState<HTMLElement | null>(null);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onFleetCountryChange = (selected: any) => {
        const candidate = selected?.detail?.value ?? selected?.target?.value;
        const newValue =
            typeof candidate === "string" && candidate.trim() !== ""
                ? candidate
                : "";
        handleFleetCountryChange(newValue);
    };

    return (
        <div className={styles.Section}>
            <Typography variant="caption1">
                {UITEXT.UserInformation.Notice}
            </Typography>
            <Divider />
            <div>
                <div className={styles.TextfieldWrapperSingle}>
                    <TextField
                        className={styles.SmallContainerSectionField}
                        name="userInformationName"
                        label="Company name (User, the company owning or renting/leasing the product)"
                        placeholder="Enter company name as it appears in customer portal"
                        size="medium"
                        value={formData.userInformationName}
                        onChange={handleTextChange}
                    />
                    <div className={styles.ErrorWrapper}>
                        {errors && errors.userInformationName && (
                            <Typography
                                variant="caption2"
                                className={styles.ErrorMessage}
                            >
                                {errors.userInformationName}
                            </Typography>
                        )}
                    </div>
                </div>
                <div className={styles.TextfieldWrapperSingle}>
                    <TextField
                        className={styles.SmallContainerSectionField}
                        name="userInformationFleetName"
                        label="Fleet name"
                        placeholder="Enter the name of the fleet as it appears in customer portal"
                        size="medium"
                        value={formData.userInformationFleetName}
                        onChange={handleTextChange}
                        ref={(ref) => setFleetNameToolTip(ref)}
                    />
                    <Tooltip
                        reference={fleetNameToolTip}
                        offset={20}
                        id="tooltip-1"
                        variant="default"
                    >
                        This information shall be provided to you by the user
                    </Tooltip>
                    <div className={styles.ErrorWrapper}>
                        {errors && errors.userInformationFleetName && (
                            <Typography
                                variant="caption2"
                                className={styles.ErrorMessage}
                            >
                                {errors.userInformationFleetName}
                            </Typography>
                        )}
                    </div>
                </div>
                <div className={styles.TextfieldWrapperSingle}>
                    <Dropdown
                        className={styles.SmallContainerSectionField}
                        label="Country where the fleet is registered"
                        onChange={onFleetCountryChange}
                        multi={false}
                        size="medium"
                        floatingLabel={false}
                        placeholder="Select country"
                        clearable
                        filterInput={true}
                        value={formData.userInformationFleetCountryOfRegistration}
                        ref={(ref) => setFleetCountryTooltip(ref)}
                    >
                        {USER_FLEET_COUNTRIES.map((country) => (
                            <DropdownOption key={country} value={country}>
                                {country}
                            </DropdownOption>
                        ))}
                    </Dropdown>
                    <Tooltip
                        reference={fleetCountryTooltip}
                        offset={20}
                        id="tooltip-fleet-country"
                        variant="default"
                    >
                        Select the country where the fleet is registered. This information shall be provided to you by the User
                    </Tooltip>
                    <div className={styles.ErrorWrapper}>
                        {errors && errors.userInformationFleetCountryOfRegistration && (
                            <Typography
                                variant="caption2"
                                className={styles.ErrorMessage}
                            >
                                {errors.userInformationFleetCountryOfRegistration}
                            </Typography>
                        )}
                    </div>
                </div>
                <div className={styles.TextfieldWrapperSingle}>
                    <TextField
                        className={styles.SmallContainerSectionField}
                        name="userInformationFleetAdminEmail"
                        label="Fleet admin email (contact person email)"
                        placeholder="Enter the email of the fleet admin in customer portal"
                        size="medium"
                        value={formData.userInformationFleetAdminEmail}
                        onChange={handleTextChange}
                        ref={(ref) => setFleetAdminEmailToolTip(ref)}
                    />
                    <Tooltip
                        reference={fleetAdminEmailToolTip}
                        offset={20}
                        id="tooltip-1"
                        variant="default"
                    >
                        This information shall be provided to you by the user
                    </Tooltip>
                    <div className={styles.ErrorWrapper}>
                        {errors && errors.userInformationFleetAdminEmail && (
                            <Typography
                                variant="caption2"
                                className={styles.ErrorMessage}
                            >
                                {errors.userInformationFleetAdminEmail}
                            </Typography>
                        )}
                    </div>
                </div>
            </div>
            <Divider />
            <NavigationButtons onBack={onBack} onNext={onNext} />
        </div>
    );
};

export default UserInformationTab;
